import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TypingMarkdown } from './components/TypingText';
import { StethoscopeIcon, SpeakerAvatar } from './components/MedicalIcons';
import Sidebar from './components/Sidebar';
import { useAuth } from './contexts/AuthContext';
import { Mic, Pause, Play, Square, CheckCircle, HelpCircle } from 'lucide-react';
import { apiClient } from './services/api';
import { CodingPanel } from './components/CodingPanel';
import { FeedbackPanel } from './components/FeedbackPanel';
import MicControl from './components/MicControl';
import LanguageControl from './components/LanguageControl';
import TemplateControl from './components/TemplateControl';
import { NoteWithCopy } from './components/NoteWithCopy';
import LowAudioWarning from './components/LowAudioWarning';
import { useRecordingSession } from './hooks/useRecordingSession';
import './App.css';

const FHIR_ENABLED = process.env.REACT_APP_FHIR_ENABLED === 'true';

const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_URL = process.env.REACT_APP_WS_URL || `${WS_PROTOCOL}//${window.location.host}/ws/transcribe`;

const REPORT_TEMPLATES = [
    { value: 'SOAP_Chart', label: 'SOAP Chart' },
    { value: 'Images_Report', label: 'Images Report' },
];

function App() {
    const { user } = useAuth();
    const [transcripts, setTranscripts] = useState([]);
    const [summary, setSummary] = useState('Waiting for input...');
    const [noteEnded, setNoteEnded] = useState(false);
    // True between End Note and the final chart landing — shows a "Finalizing
    // note…" bar over the (read-only, dimmed) provisional chart.
    const [finalizing, setFinalizing] = useState(false);
    const [previousSummary, setPreviousSummary] = useState('');
    const [error, setError] = useState(null);
    // Typed note input — mirrors the widget's "Type to the note" box: creates
    // the session typed-first, revises after End Note, appends mid-recording.
    const [noteInput, setNoteInput] = useState('');
    const [noteInputBusy, setNoteInputBusy] = useState(false);
    const [noteInputErr, setNoteInputErr] = useState('');
    const [noteInputMsg, setNoteInputMsg] = useState('');
    const [connectionAttempts, setConnectionAttempts] = useState(0);
    const [showTranscription, setShowTranscription] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(REPORT_TEMPLATES[0].value);
    const [sessionMetrics, setSessionMetrics] = useState({
        total_input_tokens: 0,
        total_output_tokens: 0,
        total_stt_time_seconds: 0,
        total_tts_time_seconds: 0
    });

    // Session management state
    const [sessions, setSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [sessionPage, setSessionPage] = useState(0);
    const [hasMoreSessions, setHasMoreSessions] = useState(false);
    const PAGE_SIZE = 25;

    // Background coding notifications — queue (one per session, stacked bottom-right)
    const [codingNotifications, setCodingNotifications] = useState([]); // [{ sessionId, title }]
    // Set of sessionIds currently being polled (for top-of-note "processing" banner)
    const [codingProcessing, setCodingProcessing] = useState(new Set());
    // Bumped when coding completes for currentSessionId — forces CodingPanel to re-fetch
    const [codingRefreshTick, setCodingRefreshTick] = useState(0);
    // An End Note is in flight (the WS ack wait, or the HTTP finalize).
    const [ending, setEnding] = useState(false);
    const endingRef = useRef(false);
    // Active tab in the note area: 'note' | 'codes' | 'feedback'
    const [activeTab, setActiveTab] = useState('note');
    // Dot badge on the Billing codes tab — true while new codes are ready but tab not yet opened
    const [codingBadge, setCodingBadge] = useState(false);
    // Map of sessionId → { cancelled, timerId } for background polling independent of displayed session
    const bgCodingPollsRef = useRef(new Map());

    // FHIR publish state
    const [publishingToHIS, setPublishingToHIS] = useState(false);
    const [fhirDocId, setFhirDocId] = useState(null);
    const [fhirPublishError, setFhirPublishError] = useState(null);

    // Language hint for Whisper; empty string = auto-detect
    const [selectedLanguage, setSelectedLanguage] = useState('');

    const scrollRafRef = useRef(null);
    const transcriptScrollRef = useRef(null);
    const transcriptEndRef = useRef(null);
    const noteEndedRef = useRef(false);
    const sessionIdRef = useRef(null);

    // Shared recording engine — mic capture + WS transport + timer + level/mute.
    // The recording plumbing was moved out of App.js into this hook; the
    // App-specific note/summary/coding state stays here and is driven off the
    // raw onMessage tap plus the onOpen/onError/onClose lifecycle callbacks.
    const rec = useRecordingSession({
        token: localStorage.getItem('auth_token') || '',
        wsUrl: WS_URL,
        mode: 'none',
        model: 'model_1',
        reportName: selectedTemplate,
        language: selectedLanguage,
        onOpen: () => {
            setConnectionAttempts(0);
            setError(null);
        },
        onMessage: (message) => handleMessage(message),
        onError: () => {
            setError('Connection error. Please check if the backend is running.');
            setConnectionAttempts(prev => prev + 1);
        },
        onClose: ({ intentional, code, reason, reconnecting, attempt, maxAttempts }) => {
            if (reconnecting) {
                // The hook is resuming the same session on its own; keep the
                // banner informative, not alarming. onOpen clears it.
                setError(`Connection lost — reconnecting (attempt ${attempt} of ${maxAttempts})…`);
                return;
            }
            if (!intentional) {
                // Unexpected disconnect: surface the error banner; the button
                // matrix treats disconnected/error with a session id as
                // paused-like, so Resume/End stay available.
                console.warn(`⚠️ Unexpected disconnect — code ${code}`);
                setError(`Connection lost (code ${code}). ${reason || 'Check your internet or backend.'}`);
            }
            // Refresh session list after a socket closes to show the updated session.
            setTimeout(() => { loadSessions(); }, 1000);
        },
    });
    // Derived: the rest of App.js reads a boolean, same as the old isConnected state.
    const isConnected = rec.connState === 'connected';

    const generateSessionId = () => {
        try {
            if (window.crypto && typeof window.crypto.randomUUID === 'function') {
                return window.crypto.randomUUID();
            }
        } catch (e) {
            // ignore
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    const ensureSessionId = () => {
        if (!sessionIdRef.current) {
            sessionIdRef.current = generateSessionId();
        }
        return sessionIdRef.current;
    };

    // Speaker color mapping
    const getSpeakerColor = (speaker) => {
        const colors = {
            'User': '#007aff',
            'Speaker_0': '#5856d6',
            'Speaker_1': '#34c759',
            'Speaker_2': '#ff9500',
            'Speaker_3': '#ff2d55',
            'Speaker_4': '#af52de'
        };
        return colors[speaker] || '#86868b';
    };

    // Session Management Functions
    // We track the in-flight request so a new loadSessions() can supersede an
    // older one that's still pending — without this, an aborted-but-not-cleared
    // request could leave loadingSessions=true forever.
    const loadSessionsReqRef = useRef(0);
    const loadSessions = async (page = 0, retry = true) => {
        const reqId = ++loadSessionsReqRef.current;
        setLoadingSessions(true);
        try {
            const fetchedSessions = await apiClient.getSessions(page * PAGE_SIZE, PAGE_SIZE);
            // Stale response — a newer loadSessions has already started.
            if (reqId !== loadSessionsReqRef.current) return;
            setSessions(fetchedSessions);
            setHasMoreSessions(fetchedSessions.length === PAGE_SIZE);
        } catch (error) {
            console.error('Failed to load sessions:', error);
            if (reqId !== loadSessionsReqRef.current) return;
            if (retry) {
                setTimeout(() => loadSessions(page, false), 3000);
            }
        } finally {
            // Only clear the spinner if this is still the latest request.
            if (reqId === loadSessionsReqRef.current) {
                setLoadingSessions(false);
            }
        }
    };

    const selectSession = async (session) => {
        try {
            // Fetch full session details
            const fullSession = await apiClient.getSession(session.id);
            
            console.log('🔍 Full session data received:', fullSession);
            console.log('🔍 Conversation history:', fullSession.conversation_history);
            console.log('🔍 Conversation history length:', fullSession.conversation_history?.length || 0);
            
            // Set current session ID and mark note as ended (so tab area shows)
            setCurrentSessionId(session.id);
            sessionIdRef.current = session.id;
            setNoteEnded(true);
            setActiveTab('note');
            setCodingBadge(false);
            
            // Load transcript
            if (fullSession.conversation_history && fullSession.conversation_history.length > 0) {
                console.log('✅ Setting transcripts with', fullSession.conversation_history.length, 'items');
                setTranscripts(fullSession.conversation_history);
            } else {
                console.log('❌ No conversation history found, clearing transcripts');
                setTranscripts([]);
            }
            
            // Load summary
            if (fullSession.session_metadata) {
                const summaryText = typeof fullSession.session_metadata === 'string' 
                    ? fullSession.session_metadata 
                    : fullSession.session_metadata.summary || 'Waiting for input...';
                setSummary(summaryText);
            } else {
                setSummary('Waiting for input...');
            }
            
            // Load metrics if available
            if (fullSession.total_input_tokens !== undefined) {
                setSessionMetrics({
                    total_input_tokens: fullSession.total_input_tokens || 0,
                    total_output_tokens: fullSession.total_output_tokens || 0,
                    total_stt_time_seconds: fullSession.total_stt_time_seconds || 0,
                    total_tts_time_seconds: fullSession.total_tts_time_seconds || 0
                });
            }
            // Show recording duration from STT time (best available approximation)
            rec.setRecordingSeconds(Math.round(fullSession.total_stt_time_seconds || 0));
            
            console.log('Session loaded:', session.id);
        } catch (error) {
            console.error('Failed to load session:', error);
            alert('Failed to load session. Please try again.');
        }
    };

    const deleteSession = async (sessionId) => {
        try {
            await apiClient.deleteSession(sessionId);
            setSessions(prev => prev.filter(s => s.id !== sessionId));
            
            // If deleted current session, clear it
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
                sessionIdRef.current = null;
                setTranscripts([]);
                setSummary('Waiting for input...');
            }
        } catch (error) {
            console.error('Failed to delete session:', error);
            alert('Failed to delete session. Please try again.');
        }
    };

    const renameSession = async (sessionId, newTitle) => {
        try {
            const updatedSession = await apiClient.updateSessionTitle(sessionId, newTitle);
            setSessions(prev => prev.map(s => s.id === sessionId ? updatedSession : s));
        } catch (error) {
            console.error('Failed to rename session:', error);
            alert('Failed to rename session. Please try again.');
        }
    };

    const startNewSession = () => {
        // Clear current session
        setCurrentSessionId(null);
        sessionIdRef.current = null;
        setTranscripts([]);
        setSummary('Waiting for input...');
        setSessionMetrics({
            total_input_tokens: 0,
            total_output_tokens: 0,
            total_stt_time_seconds: 0,
            total_tts_time_seconds: 0
        });
        setNoteEnded(false);
        noteEndedRef.current = false;
        rec.setRecordingSeconds(0);

        console.log('Started new session');
    };

    const goToSessionPage = (page) => {
        setSessionPage(page);
        loadSessions(page);
    };

    // Load sessions on mount
    useEffect(() => {
        if (user) {
            loadSessions();
        }
    }, [user]);

    // Start a background coding poll for a specific session — runs until the
    // session's coding is no longer 'pending'. Multiple sessions can poll in parallel.
    const startBackgroundCodingPoll = useCallback((sessionId) => {
        if (!sessionId) return;
        if (bgCodingPollsRef.current.has(sessionId)) return; // already polling

        const state = { cancelled: false, timerId: null };
        bgCodingPollsRef.current.set(sessionId, state);
        // Mark as processing so the top-of-note banner shows
        setCodingProcessing(prev => {
            const next = new Set(prev);
            next.add(sessionId);
            return next;
        });

        const poll = async () => {
            if (state.cancelled) return;
            try {
                const data = await apiClient.getCodingResults(sessionId);
                if (state.cancelled) return;
                console.log(`[bg-coding] ${sessionId} status=${data.status}`);
                if (data.status === 'pending') {
                    state.timerId = setTimeout(poll, 3000);
                } else {
                    bgCodingPollsRef.current.delete(sessionId);
                    setCodingProcessing(prev => {
                        const next = new Set(prev);
                        next.delete(sessionId);
                        return next;
                    });
                    // Force CodingPanel (if this session is displayed) to immediately re-fetch
                    setCodingRefreshTick(t => t + 1);
                    // Show badge dot on Billing codes tab (cleared when user opens the tab)
                    setCodingBadge(true);
                    // Always notify (regardless of currently displayed session)
                    const needsResolution = data.status === 'needs_resolution' || !!data.has_open_queries;
                    setSessions(prev => {
                        const s = prev.find(s => s.id === sessionId);
                        const title = s?.title || 'session';
                        setCodingNotifications(ns => [
                            ...ns.filter(n => n.sessionId !== sessionId),
                            { sessionId, title, needsResolution },
                        ]);
                        return prev;
                    });
                }
            } catch (err) {
                console.warn(`[bg-coding] ${sessionId} poll error:`, err);
                // 404 = session gone (e.g. empty-session cleanup). Stop the poll
                // and clear its processing state instead of looping forever.
                if (err?.status === 404) {
                    bgCodingPollsRef.current.delete(sessionId);
                    setCodingProcessing(prev => {
                        const next = new Set(prev);
                        next.delete(sessionId);
                        return next;
                    });
                    return;
                }
                if (!state.cancelled) state.timerId = setTimeout(poll, 5000);
            }
        };

        state.timerId = setTimeout(poll, 2000);
    }, []);

    // Cancel an in-flight background coding poll for a session (used when the
    // user resumes a just-ended session — the premature End Note's coding
    // shouldn't surface while they're still recording).
    const cancelBackgroundCodingPoll = useCallback((sessionId) => {
        if (!sessionId) return;
        const state = bgCodingPollsRef.current.get(sessionId);
        if (state) {
            state.cancelled = true;
            if (state.timerId) clearTimeout(state.timerId);
            bgCodingPollsRef.current.delete(sessionId);
        }
        setCodingProcessing(prev => {
            if (!prev.has(sessionId)) return prev;
            const next = new Set(prev);
            next.delete(sessionId);
            return next;
        });
        setCodingNotifications(ns => ns.filter(n => n.sessionId !== sessionId));
    }, []);

    // Cancel all background polls on unmount
    useEffect(() => {
        const polls = bgCodingPollsRef.current;
        return () => {
            polls.forEach(state => {
                state.cancelled = true;
                if (state.timerId) clearTimeout(state.timerId);
            });
            polls.clear();
        };
    }, []);

    // Post-End-Note summary refresh. The backend runs _run_final_summary
    // AFTER we close the WS (its push to the socket lands nowhere), so poll
    // the persisted note via HTTP and update the chart once a fresh summary
    // lands. Aborts if the user navigates away or starts a new recording.
    const refreshNoteAfterEnd = useCallback(async (sid, baseline) => {
        if (!sid) return;
        // Must exceed the backend's final-summary await (up to 30s in teardown),
        // otherwise the poll gives up before the fresh chart is persisted and the
        // recording tab keeps showing the stale mid-session chart.
        const TIMEOUT_MS = 40000;
        const INTERVAL_MS = 2000;
        // Cap how long the "Finalizing…" bar shows. The final-summary pass is
        // usually a few seconds (30s is just the backend's safety ceiling). If the
        // final is identical to the provisional there's nothing to swap, so stop
        // the bar after this cap and keep polling quietly for a late, differing
        // final (which then swaps in place).
        const SPINNER_CAP_MS = 12000;
        const start = Date.now();
        const spinnerTimer = setTimeout(() => setFinalizing(false), SPINNER_CAP_MS);
        try {
            while (Date.now() - start < TIMEOUT_MS) {
                await new Promise(r => setTimeout(r, INTERVAL_MS));
                if (sessionIdRef.current !== sid) return;
                try {
                    const full = await apiClient.getSession(sid);
                    const meta = full.session_metadata;
                    const text = typeof meta === 'string'
                        ? meta
                        : (meta && typeof meta === 'object' && typeof meta.summary === 'string' ? meta.summary : '');
                    if (text && text !== baseline) {
                        if (sessionIdRef.current !== sid) return;
                        setSummary(text);
                        return;
                    }
                } catch (e) {
                    // 404 = session no longer exists (e.g. empty-session cleanup) —
                    // stop polling rather than retrying until the timeout.
                    if (e?.status === 404) return;
                    console.warn('[end-note refresh] poll error:', e);
                }
            }
        } finally {
            clearTimeout(spinnerTimer);
            setFinalizing(false);
        }
    }, []);

    const startSession = async () => {
        try {
            setError(null);
            // Start Recording after End Note = resume the SAME session (user
            // realised they forgot something, etc.). Cancel the leftover bg
            // coding poll and banner so the premature End Note's results don't
            // surface as a toast mid-recording — backend mirror-cancels the
            // final_summary_task on WS reconnect.
            const isFreshStart = !sessionIdRef.current;
            // Recording again clears any leftover "Finalizing…" bar from a prior End Note.
            setFinalizing(false);
            if (noteEndedRef.current && sessionIdRef.current) {
                // Resuming after End Note on the same session — cancel the background
                // poll but keep noteEnded=true so tabs stay visible.
                cancelBackgroundCodingPoll(sessionIdRef.current);
                noteEndedRef.current = false;
            }
            // If this is a fresh start (no existing session id yet), the note
            // must reset (timer 0 handled by rec.start) even if we were just
            // viewing an older session. Pause→Resume and End→Resume keep the
            // existing id, so the timer continues (rec.resume).
            if (isFreshStart) {
                setNoteEnded(false);
            }

            const sessionId = ensureSessionId();

            // Hand transport + capture to the shared hook. onopen/onmessage/
            // onerror/onclose are wired via the hook's lifecycle callbacks above.
            if (isFreshStart) {
                await rec.start(sessionId);
            } else {
                await rec.resume(sessionId);
            }
        } catch (error) {
            console.error('Error starting session:', error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setError('Microphone access denied. Please allow microphone permissions.');
            } else if (error.name === 'NotFoundError') {
                setError('No microphone found. Please connect a microphone.');
            } else {
                setError('Failed to start session. Please try again.');
            }
        }
    };

    const stopSession = () => {
        // Intentional pause — no end_session; the server keeps the buffer so a
        // Resume reconnects and appends. The hook releases mic/socket and its
        // onClose callback refreshes the session list.
        rec.pause();
    };

    const handleMessage = (message) => {
        switch (message.type) {
            case 'connected':
                if (message.session_id) {
                    sessionIdRef.current = message.session_id;
                }
                setCodingRefreshTick(t => t + 1);
                break;

            case 'transcript':
                const newTranscript = {
                    speaker: message.speaker,
                    text: message.content,
                    timestamp: new Date(message.timestamp).toLocaleTimeString(),
                    speakerCount: message.speaker_count,
                    id: Date.now() + Math.random()
                };
                // Add directly to transcripts (no typing animation) for instant display
                setTranscripts(prev => [...prev, newTranscript]);
                break;

            case 'summary':
                setPreviousSummary(summary); // Store previous for diff
                setSummary(message.content);

                // Update session metrics if provided
                console.log('📊 Summary message received:', message);
                if (message.metrics) {
                    setSessionMetrics(message.metrics);
                }
                break;

            default:
                // Unknown message type
                break;
        }
    };

    // ONE box, one button. The text joins the conversation as a "Clinician
    // typed" turn; End Note is the only generation trigger. Typing after an
    // End Note re-opens the note (End Note button returns) so several
    // additions batch into one regeneration.
    const sendTypedNote = async () => {
        const text = noteInput.trim();
        if (!text || noteInputBusy) return;
        if (rec.connState === 'connecting' && !sessionIdRef.current) {
            setNoteInputErr('Connecting — try again in a second.');
            return;
        }
        setNoteInputBusy(true);
        setNoteInputErr('');
        setNoteInputMsg('');
        try {
            let sid = sessionIdRef.current;
            if (!sid) {
                const created = await apiClient.createSession({ reportName: selectedTemplate });
                sid = created.session_id;
                sessionIdRef.current = sid;
            }
            await apiClient.submitNoteFeedback(sid, text);
            setNoteInput('');
            setCurrentSessionId(sid);
            // Typing after an ended note re-opens it: End Note comes back.
            setNoteEnded(false);
            noteEndedRef.current = false;
            setNoteInputMsg('Added. Press End Note to update the note.');
            loadSessions();
        } catch (e) {
            setNoteInputErr(e?.message || String(e));
        } finally {
            setNoteInputBusy(false);
        }
    };

    // Stable identity: CodingPanel's poll effect depends on this callback, so
    // an inline arrow re-armed the poll on every App render (a fetch per
    // second while recording).
    const handleCodingComplete = useCallback(() => { setCodingBadge(true); }, []);

    const handleEndNote = async () => {
        // One ending per press: a second click during the ack wait used to send
        // a second end_session and overwrite the first promise's resolver, so
        // the first call never settled and the spinner stuck.
        if (endingRef.current) return;
        endingRef.current = true;
        setEnding(true);
        noteEndedRef.current = true;
        const sid = sessionIdRef.current;
        // Show the "Finalizing note…" bar immediately (covers the ack wait, WS
        // close, and the backend's final-summary pass). refreshNoteAfterEnd clears
        // it when the final lands (or after its cap).
        if (sid) setFinalizing(true);
        // Capture the last-known summary BEFORE ending so the post-end refresh
        // poll can detect when the backend's final summary differs.
        const baselineSummary = summary;

        // Let the hook decide: it also covers End Note pressed while the socket
        // is still CONNECTING (it waits for the handshake). Branching on
        // `isConnected` here finalized over HTTP while the mic kept recording
        // into a session the backend had just been told to close.
        const endedViaWs = await rec.endNote();

        if (endedViaWs) {
            // Signalled over the WS — teardown uploads the full accumulated
            // buffer and runs coding. Waiting for the ack before the close is
            // what stops the close frame racing ahead of end_session and making
            // teardown take the "pause" branch (skipping coding + upload).
        } else if (sid) {
            // WS already closed (paused state) — trigger finalize via HTTP.
            try {
                await apiClient.finalizeSession(sid);
            } catch (e) {
                console.error('Failed to finalize session via HTTP:', e);
            }
        }

        if (sid) {
            setCurrentSessionId(sid);
            startBackgroundCodingPoll(sid);
            // Pick up the final summary the backend generates after we close
            // the WS — without this, the recording tab keeps showing the last
            // mid-session chart while other tabs see the fresh one.
            refreshNoteAfterEnd(sid, baselineSummary);
        }
        setNoteEnded(true);
        endingRef.current = false;
        setEnding(false);
        // NOTE: do NOT reset recordingSeconds here. The counter represents the
        // note's OVERALL recorded duration (the same value shown in the UI timer),
        // and End→Resume keeps the same session. Zeroing it here made a short
        // follow-up edit on an already-long note falsely trip the <15s warning.
        // A genuinely new note resets the counter via startNewSession.
    };

    useEffect(() => {
        if (!transcriptScrollRef.current) return;
        transcriptScrollRef.current.scrollTop = transcriptScrollRef.current.scrollHeight;
    }, [transcripts]);

    // Ctrl+T hidden shortcut to toggle live transcription panel
    useEffect(() => {
        const handler = (e) => {
            if (e.altKey && e.key === 't') {
                e.preventDefault();
                setShowTranscription(prev => !prev);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const scrollTranscriptToBottom = () => {
        if (scrollRafRef.current) return;
        scrollRafRef.current = requestAnimationFrame(() => {
            if (transcriptScrollRef.current) {
                transcriptScrollRef.current.scrollTop = transcriptScrollRef.current.scrollHeight;
            }
            scrollRafRef.current = null;
        });
    };

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <Sidebar
                sessions={sessions}
                currentSessionId={currentSessionId}
                onSelectSession={selectSession}
                onDeleteSession={deleteSession}
                onRenameSession={renameSession}
                onNewSession={() => {
                    // Mid-recording: pause the current session (server keeps the
                    // buffer; it can be resumed from the sidebar) and start fresh.
                    if (isConnected) rec.pause();
                    startNewSession();
                }}
                loading={loadingSessions}
                page={sessionPage}
                hasMore={hasMoreSessions}
                onPageChange={goToSessionPage}
            />
            <div className="App" style={{ flex: 1, overflow: 'auto' }}>
                <div className="top-bar">
                    <div className="top-bar__controls">
                    <TemplateControl
                        templates={REPORT_TEMPLATES}
                        value={selectedTemplate}
                        onChange={setSelectedTemplate}
                    />

                    <MicControl
                        availableMics={rec.mics}
                        selectedMicId={rec.selectedMicId}
                        onSelectMic={rec.selectMic}
                        micPermission={rec.micPermission}
                        audioLevel={rec.audioLevel}
                        isRecording={isConnected}
                        recordingSeconds={rec.recordingSeconds}
                        isMuted={rec.isMuted}
                    />

                    <LanguageControl
                        value={selectedLanguage}
                        onChange={setSelectedLanguage}
                    />

                    {/* Recording action buttons — 3-state matrix */}
                    {(() => {
                        const codingInProgress = !!(currentSessionId && codingProcessing.has(currentSessionId)) || ending;
                        const endBtn = (
                            <button
                                className="rec-btn rec-btn--end"
                                onClick={handleEndNote}
                                disabled={codingInProgress}
                                title={codingInProgress ? 'Generating clinical codes…' : 'End note and run coding'}
                            >
                                <Square size={15} /> {codingInProgress ? 'Processing…' : 'End Note'}
                            </button>
                        );

                        if (isConnected) {
                            return (
                                <>
                                    <button className="rec-btn rec-btn--pause" onClick={stopSession}>
                                        <Pause size={15} /> Pause
                                    </button>
                                    {endBtn}
                                </>
                            );
                        }

                        if (noteEnded) {
                            // Resume stays available even while background coding
                            // runs — startSession cancels the bg poll and the
                            // backend mirror-cancels the final-summary task.
                            return (
                                <button className="rec-btn rec-btn--resume" onClick={startSession}>
                                    <Play size={15} /> Resume
                                </button>
                            );
                        }

                        const pausedLike = rec.connState === 'paused' ||
                            ((rec.connState === 'disconnected' || rec.connState === 'error') && !!sessionIdRef.current);
                        if (pausedLike) {
                            return (
                                <>
                                    <button className="rec-btn rec-btn--resume" onClick={startSession}>
                                        <Play size={15} /> Resume
                                    </button>
                                    {endBtn}
                                </>
                            );
                        }

                        return (
                            <>
                                <button className="rec-btn rec-btn--start" onClick={startSession}>
                                    <Mic size={15} /> Start Recording
                                </button>
                                {sessionIdRef.current && !noteEnded && (
                                    <button className="rec-btn rec-btn--end" onClick={handleEndNote} disabled={ending}>
                                        <Square size={15} /> {ending ? 'Processing…' : 'End Note'}
                                    </button>
                                )}
                            </>
                        );
                    })()}
                    </div>
                </div>

                {error && (
                    <div className="error-message">
                        <span className="error-icon">⚠️</span>
                        <span>{error}</span>
                        {connectionAttempts > 0 && (
                            <span className="error-hint">Make sure the backend is running on port 8001</span>
                        )}
                    </div>
                )}

            <div className={`dashboard-container ${!showTranscription ? 'single-column' : ''}`}>
                {/* Dashboard 1: Live Transcription */}
                {showTranscription && (
                <div className="dashboard transcript-dashboard">
                    <div className="dashboard-header">
                        <div className="header-with-icon">
                            <StethoscopeIcon size={32} />
                            <h2>Live Transcription</h2>
                        </div>
                        <span className="badge">Real-time •</span>
                    </div>
                    
                    {/* Session Metrics Display */}
                    {(sessionMetrics.total_input_tokens > 0 || sessionMetrics.total_output_tokens > 0 || sessionMetrics.total_stt_time_seconds > 0 || sessionMetrics.total_tts_time_seconds > 0) && (
                        <div className="session-metrics">
                            <div className="metrics-grid">
                                <div className="metric-item">
                                    <span className="metric-label">Input Tokens</span>
                                    <span className="metric-value">{sessionMetrics.total_input_tokens.toLocaleString()}</span>
                                </div>
                                <div className="metric-item">
                                    <span className="metric-label">Output Tokens</span>
                                    <span className="metric-value">{sessionMetrics.total_output_tokens.toLocaleString()}</span>
                                </div>
                                <div className="metric-item">
                                    <span className="metric-label">Total Tokens</span>
                                    <span className="metric-value">{(sessionMetrics.total_input_tokens + sessionMetrics.total_output_tokens).toLocaleString()}</span>
                                </div>
                                <div className="metric-item">
                                    <span className="metric-label">STT Time</span>
                                    <span className="metric-value">{sessionMetrics.total_stt_time_seconds.toFixed(1)}s</span>
                                </div>
                                <div className="metric-item">
                                    <span className="metric-label">TTS Time</span>
                                    <span className="metric-value">{sessionMetrics.total_tts_time_seconds.toFixed(1)}s</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="dashboard-content" ref={transcriptScrollRef}>
                        {transcripts.length === 0 ? (
                            <p className="empty-state">Your speech will appear here in real-time...</p>
                        ) : (
                            <div className="transcript-list">
                                {/* Already completed transcripts */}
                                {transcripts.map((item, index) => (
                                    <div
                                        key={`complete-${index}`}
                                        className="transcript-item"
                                        style={{ borderLeftColor: getSpeakerColor(item.speaker) }}
                                    >
                                        <div className="transcript-header">
                                            <div className="speaker-info">
                                                <SpeakerAvatar
                                                    speakerId={item.speaker.replace('Speaker_', '')}
                                                    color={getSpeakerColor(item.speaker)}
                                                    size={32}
                                                />
                                                <span
                                                    className="speaker-label"
                                                    style={{ color: getSpeakerColor(item.speaker) }}
                                                >
                                                    {item.speaker}
                                                </span>
                                            </div>
                                            <span className="timestamp">{item.timestamp}</span>
                                        </div>
                                        <p className="transcript-text">{item.text}</p>
                                    </div>
                                ))}
                                <div ref={transcriptEndRef} />
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* Dashboard 2: AI Summary */}
                <div className="dashboard summary-dashboard">
                    <div className="dashboard-header">
                        <div className="header-with-icon">
                            <h2>AI Notes</h2>
                        </div>
                        <div className="header-controls">
                            <span className="badge">Updates every 30s</span>
                        </div>
                    </div>

                    {/* Tab bar — always visible */}
                    <nav className="tab-bar">
                        <button
                            className={`tab-button${activeTab === 'note' ? ' tab-button--active' : ''}`}
                            onClick={() => setActiveTab('note')}
                        >
                            Visit note
                        </button>
                        <button
                            className={`tab-button${activeTab === 'codes' ? ' tab-button--active' : ''}`}
                            onClick={() => { setActiveTab('codes'); setCodingBadge(false); }}
                        >
                            Billing codes
                            {currentSessionId && codingProcessing.has(currentSessionId) && (
                                <span className="tab-coding-spinner" />
                            )}
                            {codingBadge && activeTab !== 'codes' && !codingProcessing.has(currentSessionId) && (
                                <span className="tab-badge" />
                            )}
                        </button>
                        <button
                            className={`tab-button${activeTab === 'feedback' ? ' tab-button--active' : ''}`}
                            onClick={() => setActiveTab('feedback')}
                        >
                            Feedback
                        </button>
                    </nav>

                    <div className="dashboard-content">

                        {/* ── Visit note tab panel ── */}
                        <div className="tab-panel" style={{ display: activeTab === 'note' ? 'block' : 'none' }}>
                            {/* Live transcription progress during active recording */}
                            {isConnected && (() => {
                                const wordCount = transcripts.reduce(
                                    (n, t) => n + (typeof t.text === 'string' ? t.text.trim().split(/\s+/).filter(Boolean).length : 0),
                                    0
                                );
                                const mm = String(Math.floor(rec.recordingSeconds / 60)).padStart(2, '0');
                                const ss = String(rec.recordingSeconds % 60).padStart(2, '0');
                                return (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '6px 2px',
                                        marginBottom: '10px',
                                        fontSize: '0.8rem',
                                        color: '#475569',
                                        fontWeight: 500,
                                    }}>
                                        <span>🗣 <strong style={{ color: '#0f172a' }}>{wordCount}</strong> words</span>
                                        <span style={{ opacity: 0.4 }}>·</span>
                                        <span><strong style={{ color: '#0f172a' }}>{transcripts.length}</strong> segments</span>
                                        <span style={{ opacity: 0.4 }}>·</span>
                                        <span><strong style={{ color: '#0f172a' }}>{mm}:{ss}</strong> recorded</span>
                                    </div>
                                );
                            })()}
                            <div className="summary-content">
                                {finalizing && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '10px 14px', marginBottom: 12, borderRadius: 8,
                                        background: '#eff6ff', border: '1px solid #bfdbfe',
                                        color: '#1e40af', fontSize: 13, fontWeight: 600,
                                    }}>
                                        <span style={{
                                            width: 16, height: 16, flexShrink: 0,
                                            border: '2px solid #bfdbfe', borderTopColor: '#1e293b',
                                            borderRadius: '50%', display: 'inline-block',
                                            animation: 'spin 0.8s linear infinite',
                                        }} />
                                        Finalizing note… generating your final chart.
                                    </div>
                                )}
                                {summary === 'Waiting for input...' ? (
                                    <p className="empty-state">{summary}</p>
                                ) : (
                                    <div
                                        className="summary-text medical-report"
                                        style={finalizing ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                                    >
                                        <div className="markdown-content">
                                            {isConnected ? (
                                                <div className="typing-markdown-wrapper">
                                                    <TypingMarkdown
                                                        content={summary}
                                                        previousContent={previousSummary}
                                                        typingSpeed={25}
                                                    />
                                                </div>
                                            ) : (
                                                <NoteWithCopy content={summary} />
                                            )}
                                        </div>
                                    </div>
                                )}
                                {FHIR_ENABLED && noteEnded && currentSessionId && (
                                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <button
                                            onClick={async () => {
                                                setPublishingToHIS(true);
                                                setFhirPublishError(null);
                                                try {
                                                    const result = await apiClient.publishToHIS(currentSessionId, '', '');
                                                    setFhirDocId(result.fhir_doc_id);
                                                } catch (e) {
                                                    setFhirPublishError('Failed to publish note to HIS.');
                                                } finally {
                                                    setPublishingToHIS(false);
                                                }
                                            }}
                                            disabled={publishingToHIS}
                                            style={{
                                                padding: '8px 16px', borderRadius: 6, border: 'none',
                                                background: fhirDocId ? '#d1fae5' : '#1e293b',
                                                color: fhirDocId ? '#065f46' : '#fff',
                                                fontWeight: 600, cursor: publishingToHIS ? 'default' : 'pointer',
                                                fontSize: 13,
                                            }}
                                        >
                                            {publishingToHIS ? 'Publishing…' : fhirDocId ? '✓ Sent — Send again' : '🏥 Send to HIS'}
                                        </button>
                                        {fhirDocId && (
                                            <span style={{ fontSize: 11, color: '#6b7280' }}>Doc ID: {fhirDocId}</span>
                                        )}
                                        {fhirPublishError && (
                                            <span style={{ fontSize: 12, color: '#ef4444' }}>{fhirPublishError}</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Typed note input — same behavior as the widget's box.
                                Always available: typed-first, mid-recording, or revision. */}
                            <div style={{ marginTop: 12, padding: 10, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>
                                    Type to the note
                                </div>
                                <textarea
                                    value={noteInput}
                                    onChange={e => { setNoteInput(e.target.value); setNoteInputMsg(''); }}
                                    disabled={noteInputBusy}
                                    rows={2}
                                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 6, padding: 8, font: 'inherit', fontSize: 13, resize: 'vertical' }}
                                    placeholder="Patient information or instructions"
                                />
                                {noteInputErr && (
                                    <div style={{ fontSize: 12, color: '#c33', marginTop: 4 }}>{noteInputErr}</div>
                                )}
                                {noteInputMsg && (
                                    <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>{noteInputMsg}</div>
                                )}
                                <button
                                    className="rec-btn rec-btn--start"
                                    style={{ marginTop: 6 }}
                                    onClick={sendTypedNote}
                                    disabled={noteInputBusy || !noteInput.trim()}
                                >
                                    {noteInputBusy ? 'Adding…' : 'Add to note'}
                                </button>
                            </div>
                        </div>

                        {/* ── Billing codes tab panel — always mounted, empty until coding runs ── */}
                        <div className="tab-panel" style={{ display: activeTab === 'codes' ? 'block' : 'none' }}>
                            {currentSessionId ? (
                                <CodingPanel
                                    sessionId={currentSessionId}
                                    refreshTick={codingRefreshTick}
                                    onCodingComplete={handleCodingComplete}
                                />
                            ) : (
                                <p className="empty-state">Start a recording to generate billing codes.</p>
                            )}
                        </div>

                        {/* ── Feedback tab panel — always mounted ── */}
                        <div className="tab-panel" style={{ display: activeTab === 'feedback' ? 'block' : 'none' }}>
                            {currentSessionId ? (
                                <FeedbackPanel
                                    sessionId={currentSessionId}
                                    onApplied={(update) => {
                                        // Push the updated chart back to the note pane so the
                                        // clinician sees their addition immediately. Without
                                        // this, the chart only refreshes when they switch
                                        // sessions and come back.
                                        if (update?.note_text) {
                                            setSummary(update.note_text);
                                        }
                                        // Bump the coding panel so it re-polls and picks up
                                        // any code changes (dx/px routes).
                                        setCodingRefreshTick(t => t + 1);
                                        setCodingBadge(true);
                                    }}
                                />
                            ) : (
                                <p className="empty-state">Start a recording to enable feedback.</p>
                            )}
                        </div>

                    </div>
                </div>
            </div>


            <footer className="app-footer">
                {(currentSessionId || sessionIdRef.current) && (
                    <p style={{
                        fontSize: 11, color: '#9ca3af', fontFamily: 'monospace',
                        userSelect: 'all', margin: '0 0 4px',
                    }}>
                        Session ID: {currentSessionId || sessionIdRef.current}
                    </p>
                )}
                <p>Powered by Optimize AI</p>
            </footer>

            {/* Background coding completion toasts — stacked bottom-right */}
            {codingNotifications.map((notif, i) => {
                const isCurrent = notif.sessionId === currentSessionId;
                const dismiss = () => setCodingNotifications(ns => ns.filter(n => n.sessionId !== notif.sessionId));
                return (
                    <div
                        key={notif.sessionId}
                        style={{
                            position: 'fixed', bottom: 24 + i * 130, right: 24, zIndex: 9999,
                            background: '#1e293b', color: '#fff', borderRadius: 10,
                            padding: '14px 18px', width: 320,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                            display: 'flex', flexDirection: 'column', gap: 8,
                            animation: 'slideIn 0.25s ease-out',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {notif.needsResolution
                                ? <HelpCircle size={16} color="#f59e0b" />
                                : <CheckCircle size={16} color="#22c55e" />}
                            <span style={{ fontWeight: 600, fontSize: 13 }}>
                                {notif.needsResolution ? 'Replies required' : 'Coding ready'}
                            </span>
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                            {notif.needsResolution
                                ? <>Some clarifications for <em>{notif.title}</em> need your answers before codes are final.</>
                                : <>Clinical codes for <em>{notif.title}</em> are ready to review.</>}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={() => {
                                    if (isCurrent) {
                                        setActiveTab('codes');
                                        setCodingBadge(false);
                                    } else {
                                        const s = sessions.find(s => s.id === notif.sessionId);
                                        if (s) {
                                            selectSession(s).then(() => {
                                                setActiveTab('codes');
                                                setCodingBadge(false);
                                            });
                                        }
                                    }
                                    dismiss();
                                }}
                                style={{
                                    flex: 1, padding: '6px 0', borderRadius: 6, border: 'none',
                                    background: '#1e293b', color: '#fff', fontWeight: 600,
                                    fontSize: 12, cursor: 'pointer',
                                }}
                            >
                                {notif.needsResolution ? 'Answer questions' : 'View codes'}
                            </button>
                            <button
                                onClick={dismiss}
                                style={{
                                    padding: '6px 12px', borderRadius: 6, border: 'none',
                                    background: '#334155', color: '#fff', fontSize: 12, cursor: 'pointer',
                                }}
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                );
            })}
            </div>

            {/* Popup when the mic stays low/muted for a sustained stretch */}
            <LowAudioWarning isRecording={isConnected} isMuted={rec.isMuted} />

        </div>
    );
}

export default App;
